// Makkerlaget oven på kortet: makkerens markør, pings, pinghjulet, spøgelser for egne handlinger på
// vej til rummet, og et lille mærke med forbindelsen. Kun DOM; positionerne kommer fra online.ts.

import type { P } from '../render/geo';
import type { NetStatus } from '../net/room-client';
import type { PingAt, PingKind, Seat } from '../net/protocol';
import type { ActionKind } from '../sim/types';
import { h, toggle } from './dom';
import { ROLE, seatMark } from './screens/room';

export const PING_LABEL: Record<PingKind, string> = {
  build: 'Her!',
  help: 'Hjælp!',
  mine: 'Jeg tager den',
  nice: 'Godt gået',
  danger: 'Fare',
  ask: 'Kan du?',
};

/**
 * Spor: coop: spørgsmålet til makkeren om noget, kun den anden rolle må. Teksten siger præcis, hvad der
 * bedes om, så "Byg her?" ikke kan læses som "Fare".
 */
const ASK_LABEL: Partial<Record<ActionKind, string>> = {
  build_line: 'Byg her?',
  bury_line: 'Kabel her?',
  upgrade_station: 'Flere pladser her?',
  build_battery: 'Batteri her?',
  build_flywheel: 'Svinghjul her?',
  demolish: 'Riv ned?',
  power_shed: 'Brug nødknappen?',
  reinforce_mast: 'Forstærk her?',
  upgrade_line: 'Opgradér her?',
  dispatch_crew: 'Montør her?',
  hire_crew: 'Hyr en montør?',
  shoo_threat: 'Jag den væk?',
  answer_card: 'Svar på kortet?',
  power_fcr: 'Brug Lynreserven?',
  power_norway: 'Ring til Norge?',
};

export function pingLabel(kind: PingKind, act?: ActionKind): string {
  return kind === 'ask' && act ? (ASK_LABEL[act] ?? PING_LABEL.ask) : PING_LABEL[kind];
}

/** Rækkefølgen i hjulet, med uret fra toppen. Tallene 1-5 vælger. */
const WHEEL: readonly PingKind[] = ['build', 'help', 'mine', 'nice', 'danger'];
const PING_MS = 4200;
/** "Jeg tager den" står længere: det er en aftale, ikke et råb. Samme tid som makkerens krav i online.ts. */
export const CLAIM_MS = 12_000;
const LONG_PRESS_MS = 600;
/** På en node starter et træk en ledning, og man hviler tit fingeren før man trækker: kræv et længere tryk dér. */
const LONG_PRESS_NODE_MS = 1500;
/** Så langt må fingeren glide, før et langt tryk bliver et træk (på en node lidt mindre). */
const PRESS_SLOP = 10;
const PRESS_SLOP_NODE = 6;
/** Pingboblen holder sig så langt fra skærmens kant, og flipper ned under målet tæt på toppen. */
const PING_EDGE = 8;
const PING_TOP = 72;

export type GhostShape = { line: [string, string] } | { at: PingAt };

interface PingView {
  el: HTMLElement;
  stack: HTMLElement;
  /** Boblens bredde og højde, målt første gang den vises. */
  size: { w: number; h: number } | null;
  at: PingAt;
  until: number;
}

interface GhostView {
  shape: GhostShape;
  el: SVGElement;
  until: number;
}

export interface CoopDeps {
  /** Et ping-mål (id eller kortpunkt) til skærmkoordinater i vinduet, eller null hvis det ikke findes. */
  locate(at: PingAt): P | null;
  /** Hvad der ligger under et punkt i vinduet: en node, ledning, trussel eller bare et kortpunkt. */
  anchor(p: P): PingAt | null;
  /** Send et ping. to: id på det ping, der svares på. act: handlingen, et 'ask' beder om. */
  ping(kind: PingKind, at: PingAt, to?: number, act?: ActionKind): void;
  /** Må hjulet åbne lige nu (en bane i gang uden skærm foran)? */
  live(): boolean;
  /** Ligger der en node under punktet (så et tryk dér kan blive starten på en ledning)? */
  onNode?(p: P): boolean;
  /** Et lille ryk, når hjulet åbner med et langt tryk (følger indstillingen Vibration). */
  vibrate?(ms: number): void;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

export class CoopLayer {
  readonly el = h('div', { class: 'coop', hidden: true });
  /**
   * Hjulet står for sig selv i roden, ikke i .coop: co-op runde 5 så det åbne bag hændelseskortet og
   * nødknapperne, fordi .coop (z 9) er sin egen stabel. Nu ligger det øverst over alt (z 45).
   */
  readonly wheelEl: HTMLElement;
  private svg = document.createElementNS(SVG_NS, 'svg');
  private cursor: HTMLElement;
  private cursorAt: P | null = null;
  private cursorGoal: P | null = null;
  private cursorSeen = 0;
  private badge: HTMLElement;
  private badgeText: HTMLElement;
  private wheel: HTMLElement;
  private wheelAt: PingAt | null = null;
  private pings = new Map<number, PingView>();
  private ghosts = new Map<number, GhostView>();
  private you: Seat = 'p1';
  private lastPointer: P | null = null;
  private press: { id: number; x: number; y: number; timer: number; slop: number } | null = null;
  /**
   * Hjulet åbnet med et langt tryk, mens fingeren stadig er nede. Co-op-anmelderen (IGN-løkke runde 3):
   * slippet sendte et ping, han ikke havde valgt. Nu vælger man ved at glide ud til et ord og slippe;
   * slipper man i midten, står hjulet bare åbent, og et tryk på prikken eller ved siden af lukker det.
   */
  private held: { id: number; x: number; y: number } | null = null;
  private openedAt = 0;

  constructor(
    private stage: HTMLElement,
    private deps: CoopDeps,
  ) {
    this.svg.setAttribute('class', 'coop-ghosts');
    this.svg.setAttribute('aria-hidden', 'true');
    this.cursor = h('div', { class: 'coop-cursor', hidden: true, 'aria-hidden': 'true' });
    this.badgeText = h('span', { class: 'badge-text' }, '');
    this.badge = h('div', { class: 'coop-badge', role: 'status', hidden: true }, h('span', { class: 'marks' }), this.badgeText);
    this.wheel = h('div', { class: 'ping-wheel', hidden: true, role: 'menu', 'aria-label': 'Sig noget til din makker' });
    window.addEventListener('pointermove', this.onHeldMove, true);
    window.addEventListener('pointerup', this.onHeldUp, true);
    window.addEventListener('pointercancel', this.onHeldCancel, true);
    this.wheel.addEventListener('pointerdown', (e) => {
      if (e.target === this.wheel) this.closeWheel();
    });
    this.wheel.addEventListener('contextmenu', (e) => e.preventDefault());
    this.wheelEl = this.wheel;
    this.el.append(this.svg, this.cursor, this.badge);

    stage.addEventListener('pointermove', this.onMove);
    stage.addEventListener('pointerdown', this.onDown);
    stage.addEventListener('pointerup', this.cancelPress);
    stage.addEventListener('pointercancel', this.cancelPress);
    stage.addEventListener('contextmenu', this.onContext);
    // Capture: når hjulet er åbent, går 1-5 og Esc til hjulet og ikke til værktøjerne.
    window.addEventListener('keydown', this.onKey, true);
  }

  /** Markørens bevægelser, til online.ts (som sender dem med højst 10 pr. s). */
  onPointer: ((p: P | null) => void) | null = null;

  setSeat(you: Seat): void {
    this.you = you;
    const other: Seat = you === 'p1' ? 'p2' : 'p1';
    this.cursor.replaceChildren(seatMark(other), h('span', {}, 'Makker'));
    const marks = this.badge.querySelector('.marks') as HTMLElement;
    marks.replaceChildren(seatMark(you), seatMark(other, 'partner'));
  }

  /** Spor: coop: spørg makkeren om noget, kun den anden rolle må ("Byg her?"). */
  ask(at: PingAt, act: ActionKind): void {
    if (this.deps.live()) this.deps.ping('ask', at, undefined, act);
  }

  /** Vis eller skjul hele laget (kun mens en makkerbane er i gang). */
  show(on: boolean): void {
    this.el.hidden = !on;
    this.badge.hidden = !on;
    if (!on) {
      this.closeWheel();
      this.clear();
    }
  }

  /**
   * Spor: telefonkort: mærket står i knaprækken øverst (til venstre for pause og lyd), ikke på
   * kortet, hvor det dækkede Vardes "Strøm, tak!" på gæstens telefon.
   */
  private badgeKey = '';

  dockBadge(host: Element | null): void {
    if (host && this.badge.parentNode !== host) host.prepend(this.badge);
  }

  setStatus(partnerOn: boolean, net: NetStatus): void {
    // Rollen står i mærket hele banen (IGN-løkke runde 4), så ingen glemmer, hvem der gør hvad.
    // På en smal telefon vises kun rollen; resten står der stadig til skærmlæseren (CSS).
    const away = net === 'reconnecting' || net === 'connecting' ? 'Forbinder igen…' : partnerOn ? null : 'Makker er lige væk';
    const key = away ?? `${this.you}`;
    if (key !== this.badgeKey) {
      this.badgeKey = key;
      if (away) this.badgeText.replaceChildren(h('span', { class: 'bt-role' }, away));
      else this.badgeText.replaceChildren(h('span', { class: 'bt-role' }, ROLE[this.you].name), h('span', { class: 'bt-rest' }, ' · makker er med'));
    }
    toggle(this.badge, 'away', !partnerOn || net !== 'open');
  }

  // -------------------------------------------------------------------------
  // Makkerens markør

  cursorTarget(p: P | null, now: number): void {
    this.cursorGoal = p;
    this.cursorSeen = now;
    if (p && !this.cursorAt) this.cursorAt = { ...p };
  }

  // -------------------------------------------------------------------------
  // Pings

  showPing(id: number, from: Seat, kind: PingKind, at: PingAt, now: number, canReply: boolean, to?: number, act?: ActionKind): void {
    const mine = from === this.you;
    // Et svar ("Jeg tager den") afløser det ping, det svarer på, så de ikke ligger oven i hinanden.
    const answered = to !== undefined ? this.pings.get(to) : undefined;
    if (answered && to !== undefined) {
      answered.el.remove();
      this.pings.delete(to);
    }
    const label = pingLabel(kind, act);
    const reply =
      canReply && !mine && (kind === 'help' || kind === 'build' || kind === 'danger' || kind === 'ask')
        ? h(
            'button',
            {
              type: 'button',
              class: 'ping-reply',
              onclick: (e: Event) => {
                e.stopPropagation();
                this.deps.ping('mine', at, id);
                (e.currentTarget as HTMLElement).remove();
              },
            },
            'Jeg tager den',
          )
        : null;
    // Boblen og svarknappen står samlet over målet (knappen under navnet), så målet kan ses.
    const stack = h('div', { class: 'ping-stack' }, h('span', { class: 'ping-bubble' }, seatMark(from), h('span', {}, label)), reply);
    const el = h('div', { class: `ping ${kind} ${from}${mine ? ' own' : ''}`, role: 'status' }, h('span', { class: 'ping-ring', 'aria-hidden': 'true' }), stack);
    this.el.append(el);
    // Højst tre pings på én gang; det ældste går først.
    if (this.pings.size >= 3) {
      const [oldId, old] = this.pings.entries().next().value as [number, PingView];
      old.el.remove();
      this.pings.delete(oldId);
    }
    // Et spørgsmål til makkeren står lidt længere end et råb: det skal nås at læse og gøre.
    this.pings.set(id, { el, stack, size: null, at, until: now + (kind === 'mine' ? CLAIM_MS : kind === 'ask' ? PING_MS * 2 : PING_MS) });
  }

  // -------------------------------------------------------------------------
  // Spøgelser: egne handlinger, rummet ikke har udført endnu.

  addGhost(seq: number, shape: GhostShape, now: number): void {
    const isLine = 'line' in shape;
    const el = document.createElementNS(SVG_NS, isLine ? 'path' : 'circle');
    el.setAttribute('class', `ghost ${this.you}`);
    this.svg.append(el);
    this.ghosts.set(seq, { shape, el, until: now + 4000 });
  }

  removeGhost(seq: number): void {
    const g = this.ghosts.get(seq);
    if (!g) return;
    g.el.remove();
    this.ghosts.delete(seq);
  }

  hasGhost(seq: number): boolean {
    return this.ghosts.has(seq);
  }

  clear(): void {
    for (const g of this.ghosts.values()) g.el.remove();
    this.ghosts.clear();
    for (const p of this.pings.values()) p.el.remove();
    this.pings.clear();
    this.cursorGoal = null;
    this.cursorAt = null;
  }

  // -------------------------------------------------------------------------
  // Pinghjulet: langt tryk, højreklik eller G.

  get wheelOpen(): boolean {
    return !this.wheel.hidden;
  }

  openWheel(p: P, instant = false): void {
    if (!this.deps.live()) return;
    const at = this.deps.anchor(p);
    if (!at) return;
    this.wheelAt = at;
    // En flad ellipse, så de brede ord nederst ikke støder sammen; smallere på en smal telefon.
    const rx = Math.max(96, Math.min(130, window.innerWidth / 2 - 80));
    const ry = 80;
    const mx = rx + 70;
    const my = ry + 34;
    // IGN-løkke runde 4: på en telefon blev hjulet klemt ind fra kanten, så "Hjælp!" lå under fingeren
    // og pingen så ud til at gælde en anden by. Nu står prikken altid på fingeren, og ordene står i en
    // søjle ved siden af, ud mod skærmens midte.
    const narrow = window.innerWidth < 600;
    const W = window.innerWidth;
    const H = window.innerHeight;
    const cx = narrow ? Math.max(24, Math.min(W - 24, p.x)) : Math.max(Math.min(mx, W / 2), Math.min(W - mx, p.x));
    const cy = narrow ? Math.max(24, Math.min(H - 24, p.y)) : Math.max(my, Math.min(H - my, p.y));
    const toLeft = cx > W / 2;
    const gapY = 50;
    const y0 = Math.max(70, Math.min(H - 70 - (WHEEL.length - 1) * gapY, cy - ((WHEEL.length - 1) * gapY) / 2));
    const items = WHEEL.map((kind, i) => {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / WHEEL.length;
      // --ox/--oy: en tredjedel af vejen ind mod midten. Hjulet folder sig ud fra fingeren.
      const ix = narrow ? cx + (toLeft ? -34 : 34) : cx + Math.cos(a) * rx;
      const iy = narrow ? y0 + i * gapY : cy + Math.sin(a) * ry;
      const ox = Math.round(narrow ? (toLeft ? 20 : -20) : -Math.cos(a) * rx * 0.35);
      const oy = Math.round(narrow ? (cy - iy) * 0.35 : -Math.sin(a) * ry * 0.35);
      return h(
        'button',
        {
          type: 'button',
          role: 'menuitem',
          class: `wheel-item ${kind}${narrow ? (toLeft ? ' col-left' : ' col-right') : ''}`,
          'data-kind': kind,
          style: `left:${Math.round(ix)}px;top:${Math.round(iy)}px;--i:${i};--ox:${ox}px;--oy:${oy}px`,
          // Et klik lige efter åbningen er slippet fra det lange tryk, ikke et valg.
          onclick: (e: Event) => {
            if (this.fromPress(e)) return;
            this.choose(kind);
          },
        },
        h('kbd', { class: 'kbd', 'aria-hidden': 'true' }, String(i + 1)),
        PING_LABEL[kind],
      );
    });
    // Prikken i midten lukker hjulet: "fortryd" ligger lige under fingeren.
    // IGN-løkke 2, runde 3 (co-op): på telefonen kom det kompatible klik fra fingeren, der slap efter det
    // lange tryk, og ramte prikken lige under den. Hjulet lukkede igen, før man nåede at vælge.
    const dot = h('button', {
      type: 'button',
      class: 'wheel-dot',
      style: `left:${Math.round(cx)}px;top:${Math.round(cy)}px`,
      'aria-label': 'Luk uden at sige noget',
      onclick: (e: Event) => {
        if (this.fromPress(e)) return;
        this.closeWheel();
      },
    });
    this.wheel.replaceChildren(dot, ...items);
    this.openedAt = performance.now();
    // Åbnet med tastaturet (G): ingen bevægelse, menuen står der bare.
    this.wheel.classList.toggle('instant', instant);
    this.wheel.hidden = false;
    document.body.classList.add('wheel-open');
    (items[0] as HTMLElement).focus({ preventScroll: true });
  }

  closeWheel(): void {
    if (this.wheel.hidden) return;
    this.wheel.hidden = true;
    this.held = null;
    document.body.classList.remove('wheel-open');
    this.wheel.replaceChildren();
    this.wheelAt = null;
    this.stage.focus({ preventScroll: true });
  }

  private choose(kind: PingKind): void {
    const at = this.wheelAt;
    this.closeWheel();
    if (at) this.deps.ping(kind, at);
  }

  private local(e: PointerEvent | MouseEvent): P {
    return { x: e.clientX, y: e.clientY };
  }

  private onMove = (e: PointerEvent): void => {
    const p = this.local(e);
    this.lastPointer = p;
    this.onPointer?.(p);
    if (this.press && e.pointerId === this.press.id && Math.hypot(p.x - this.press.x, p.y - this.press.y) > this.press.slop) this.cancelPress();
  };

  private onDown = (e: PointerEvent): void => {
    this.lastPointer = this.local(e);
    this.cancelPress();
    // Langt tryk kun med finger og pen; med musen holder man knappen nede for at trække ledninger.
    if (e.pointerType === 'mouse' || !this.deps.live()) return;
    const id = e.pointerId;
    const { x, y } = this.local(e);
    const node = this.deps.onNode?.({ x, y }) ?? false;
    const timer = window.setTimeout(() => {
      this.press = null;
      // Afbryd det træk eller tryk, kortet troede var i gang, så hjulet ikke også bygger noget.
      this.stage.dispatchEvent(new PointerEvent('pointercancel', { pointerId: id, clientX: x, clientY: y }));
      this.deps.vibrate?.(12);
      this.openWheel({ x, y });
      if (this.wheelOpen) this.held = { id, x, y };
    }, node ? LONG_PRESS_NODE_MS : LONG_PRESS_MS);
    this.press = { id, x, y, timer, slop: node ? PRESS_SLOP_NODE : PRESS_SLOP };
  };

  private itemAt(x: number, y: number): HTMLElement | null {
    const el = document.elementFromPoint(x, y);
    const item = el instanceof Element ? el.closest('.wheel-item') : null;
    return item instanceof HTMLElement && this.wheel.contains(item) ? item : null;
  }

  private onHeldMove = (e: PointerEvent): void => {
    const held = this.held;
    if (!held || e.pointerId !== held.id || !this.wheelOpen) return;
    const item = Math.hypot(e.clientX - held.x, e.clientY - held.y) > 24 ? this.itemAt(e.clientX, e.clientY) : null;
    for (const b of this.wheel.querySelectorAll('.wheel-item')) b.classList.toggle('hot', b === item);
  };

  /** Er klikket bare fingeren, der slap efter det lange tryk (eller lige efter åbningen)? */
  private fromPress(e: Event): boolean {
    if ((e as MouseEvent).detail <= 0) return false;
    const now = performance.now();
    return now - this.openedAt < 350 || now - this.releasedAt < 450;
  }

  /** Da fingeren, der åbnede hjulet med et langt tryk, slap. */
  private releasedAt = -Infinity;

  private onHeldUp = (e: PointerEvent): void => {
    const held = this.held;
    if (!held || e.pointerId !== held.id) return;
    this.held = null;
    this.releasedAt = performance.now();
    if (!this.wheelOpen) return;
    // Sluppet i midten: intet ping. Hjulet bliver stående, så man kan trykke et ord eller lukke det.
    if (Math.hypot(e.clientX - held.x, e.clientY - held.y) <= 24) return;
    const kind = this.itemAt(e.clientX, e.clientY)?.dataset.kind as PingKind | undefined;
    e.preventDefault();
    e.stopPropagation();
    if (kind && (WHEEL as readonly string[]).includes(kind)) this.choose(kind);
    else this.closeWheel();
  };

  private onHeldCancel = (e: PointerEvent): void => {
    if (this.held && e.pointerId === this.held.id && e.isTrusted) this.held = null;
  };

  private cancelPress = (): void => {
    if (!this.press) return;
    window.clearTimeout(this.press.timer);
    this.press = null;
  };

  private onContext = (e: MouseEvent): void => {
    e.preventDefault();
    this.openWheel(this.local(e));
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
    if (this.wheelOpen) {
      const i = Number(e.key) - 1;
      if (Number.isInteger(i) && i >= 0 && i < WHEEL.length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.choose(WHEEL[i] as PingKind);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.closeWheel();
      }
      return;
    }
    if ((e.key === 'g' || e.key === 'G') && this.deps.live()) {
      e.preventDefault();
      const r = this.stage.getBoundingClientRect();
      this.openWheel(this.lastPointer ?? { x: r.left + r.width / 2, y: r.top + r.height / 2 }, true);
    }
  };

  // -------------------------------------------------------------------------

  frame(now: number, dt: number): void {
    // Markøren glider mod det sidste punkt; efter 5 s uden nyt forsvinder den.
    const goal = this.cursorGoal;
    const stale = now - this.cursorSeen > 5000;
    if (!goal || stale || !this.cursorAt) {
      this.cursor.hidden = true;
    } else {
      const k = 1 - Math.exp(-dt / 70);
      this.cursorAt.x += (goal.x - this.cursorAt.x) * k;
      this.cursorAt.y += (goal.y - this.cursorAt.y) * k;
      this.cursor.hidden = false;
      this.cursor.style.transform = `translate(${Math.round(this.cursorAt.x)}px, ${Math.round(this.cursorAt.y)}px)`;
    }
    for (const [id, p] of this.pings) {
      if (now > p.until) {
        p.el.remove();
        this.pings.delete(id);
        continue;
      }
      const s = this.deps.locate(p.at);
      p.el.hidden = !s;
      if (!s) continue;
      p.el.style.transform = `translate(${Math.round(s.x)}px, ${Math.round(s.y)}px)`;
      // Hold boblen inde på skærmen: skub den sidelæns ved kanten, og vend den ned tæt på toppen.
      if (!p.size || p.size.w === 0) p.size = { w: p.stack.offsetWidth, h: p.stack.offsetHeight };
      const half = p.size.w / 2;
      const vw = window.innerWidth;
      const dx = s.x - half < PING_EDGE ? PING_EDGE - (s.x - half) : s.x + half > vw - PING_EDGE ? vw - PING_EDGE - (s.x + half) : 0;
      p.stack.style.setProperty('--dx', `${Math.round(dx)}px`);
      const below = s.y - 26 - p.size.h < PING_TOP;
      if (p.el.classList.contains('below') !== below) p.el.classList.toggle('below', below);
    }
    for (const [seq, g] of this.ghosts) {
      if (now > g.until) {
        this.removeGhost(seq);
        continue;
      }
      if ('line' in g.shape) {
        const a = this.deps.locate(g.shape.line[0]);
        const b = this.deps.locate(g.shape.line[1]);
        if (!a || !b) continue;
        g.el.setAttribute('d', `M${a.x.toFixed(1)} ${a.y.toFixed(1)}L${b.x.toFixed(1)} ${b.y.toFixed(1)}`);
      } else {
        const s = this.deps.locate(g.shape.at);
        if (!s) continue;
        g.el.setAttribute('cx', s.x.toFixed(1));
        g.el.setAttribute('cy', s.y.toFixed(1));
        g.el.setAttribute('r', '18');
      }
    }
  }
}
